import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import React, { useEffect, useMemo, useState } from 'react';
import comm_imgs from '../../../../comm_imgs';
import UiIntegrater from '../littleUIComponent/UiIntegrater';
import HisFourIndicatorsChart, { ActivityData } from './HisFourIndicatorsChart';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../../stores/hooks';
import { convertTimestamp } from './HisMobility';
import ApiGo_TS from '../../../../actions/ApiGo_TS';
import { ADD_RESPONSE } from '../../../../stores/reducer_add';
import { ADD_HIS_ALERTS } from '../../../../stores/reducer_HistoryData';
import { convertDatestamp } from './HisMobilityByDays';
interface MobilityData {
  isOldData: boolean;
  newResult: {
    residentInfo?: string[];
    [key: string]: any;
  };
  oldResult: any[];
}

const HisSixIndex = ({ data, activityData, days_open, setDays_open }: any): React.JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const id: number = data.id;
  // ------- redux --------------------
  const [chartSwitch, setChartSwitch] = useState(0);
  const store_timestamp = useAppSelector((state) => state.store_historyData.timestamp);

  const [mobilityData, setMobilityData] = useState<MobilityData>({
    isOldData: false,
    newResult: {
      sitUpCount: [0, '0:00:00', []],
      lieDownCount: [0, '0:00:00', []],
      leaveBedCount: [0, '0:00:00', []],
      turnOverCount: [0, '0:00:00', []],
    },
    oldResult: [],
  });
  useEffect(() => {
    if (days_open === 'FORDAYS') {
      fetchData();
    } else {
      setMobilityData((prev) => ({
        ...prev,
        isOldData: false,
        newResult: activityData.newResult,
      }));
    }
  }, [store_timestamp, days_open, activityData]);

  // 多日選擇 api getMobilityDataForDays
  const fetchData = async () => {
    if (store_timestamp.length === 0 || !days_open) return;
    const dateTime = convertDatestamp(store_timestamp);
    try {
      const res = await ApiGo_TS.getMobilityDataForDays(id, dateTime);
      const isDataEmpty = isAllDataEmpty(res as { sitUpCount: any[]; lieDownCount: any[]; leaveBedCount: any[]; turnOverCount: any[] });
      if (isDataEmpty) {
        dispatch(ADD_RESPONSE({ status: 404, statusText: 'Not Found', type: 'hisRec_no_data' }));
        dispatch(ADD_HIS_ALERTS([]));
      }
      setMobilityData((pre) => ({
        ...pre,
        isOldData: false,
        newResult: res as MobilityData['newResult'],
      }));
    } catch (error) {
      console.log('error', error);
    }
  };

  // 翻譯屬性名稱(用於生成Excel文件)
  const translations: { [key: string]: string } = useMemo(
    () => ({
      sitUpCount: t('HISTORY_RECORD.MOBILITY_SITTING'),
      leaveBedCount: t('HISTORY_RECORD.MOBILITY_NOT_IN_BED'),
      lieDownCount: t('HISTORY_RECORD.MOBILITY_LYING'),
      turnOverCount: t('HISTORY_RECORD.MOBILITY_ROLL'),
    }),
    [t]
  );

  const exportToExcel = async () => {
    const filteredData = filterArrays(mobilityData.newResult);
    const { residentInfo } = mobilityData.newResult;

    try {
      const workbook = new ExcelJS.Workbook();

      Object.entries(filteredData).forEach(([key, value]) => {
        if (value[0] === '0' && value[1] === '0:00:00' && value[2].length === 0) {
          return;
        }

        const worksheet = workbook.addWorksheet(translations[key] || key);

        // 添加標題
        worksheet.mergeCells('A1:D1');
        const titleCell = worksheet.getCell('A1');
        titleCell.value = translations[key] || key;
        titleCell.font = { bold: true, size: 24 };
        titleCell.alignment = { vertical: 'middle', horizontal: 'center' };

        // 添加次數和總時間
        worksheet.mergeCells('A2:B2');
        worksheet.mergeCells('C2:D2');
        const countCell = worksheet.getCell('A2');
        countCell.value = `${t('GLOBAL.TIMES')}: ${value[0]}`;
        countCell.font = { bold: true, size: 18 };
        countCell.alignment = { vertical: 'middle', horizontal: 'center' };
        const timeCell = worksheet.getCell('C2');
        timeCell.value = `${t('HISTORY_RECORD.MOBILITY_TOTAL_TIME')}: ${value[1]}`;
        timeCell.font = { bold: true, size: 18 };
        timeCell.alignment = { vertical: 'middle', horizontal: 'center' };
        // 添加表頭
        const headerRow = worksheet.addRow([t('GLOBAL.DATE'), t('HISTORY_RECORD.MOBILITY_START'), t('HISTORY_RECORD.MOBILITY_END'), t('HISTORY_RECORD.MOBILITY_DURATION')]);

        // 設置列寬
        worksheet.columns = [
          { key: 'date', width: 20 },
          { key: 'startTime', width: 20 },
          { key: 'endTime', width: 20 },
          { key: 'duration', width: 20 },
        ];

        // 添加數據
        value[2].forEach((row: any) => {
          worksheet.addRow(row);
        });

        // 設置日期格式
        worksheet.getColumn('A').numFmt = 'yyyy-mm-dd';

        // 設置所有單元格居中對齊
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber > 2) {
            // 只針對內文
            row.eachCell((cell) => {
              cell.font = { size: 16 };
              cell.alignment = { vertical: 'middle', horizontal: 'center' };
            });
          }
        });
        headerRow.font = { bold: true, size: 16 };
        headerRow.eachCell((cell) => {
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const fileName = residentInfo ? `${residentInfo[0]}_${residentInfo[1]}_${t('HISTORY_RECORD.MOBILITY_INDEX')}.xlsx` : `${t('HISTORY_RECORD.MOBILITY_INDEX')}.xlsx`;
      saveAs(new Blob([buffer], { type: 'application/octet-stream' }), fileName);
    } catch (error) {
      console.error('Error generating Excel file:', error);
    }
  };
  return (
    <>
      <UiIntegrater OnOff_switch={[true, false, false, true, true]} date={new Date()} name={data !== undefined ? data.last_name : ''} bed={data !== undefined ? data.first_name : ''} fordays={true} days_open={days_open} setDays_open={setDays_open}/>
      {!mobilityData?.isOldData &&
      isAllDataEmpty({
        sitUpCount: mobilityData.newResult.sitUpCount || [0, '0:00:00', []],
        lieDownCount: mobilityData.newResult.lieDownCount || [0, '0:00:00', []],
        leaveBedCount: mobilityData.newResult.leaveBedCount || [0, '0:00:00', []],
        turnOverCount: mobilityData.newResult.turnOverCount || [0, '0:00:00', []],
      }) ? (
        <div className="font-xl pad-30 t l">{t('GLOBAL.NO_DATA')}</div>
      ) : (
        <>
          {mobilityData.isOldData ? (
            <div className="fx-d-c h-85 pad-15 l">
              <div className="frame-table">
                {mobilityData.oldResult.map((res: any, index: number) => (
                  <div key={index}>{res}</div>
                ))}
              </div>
            </div>
          ) : (
            <div className="fx-d-c w-100 h-85 hisChart pos-r">
              <div className="pre_empty pos-a r-0">
                <div className="w-30 fx-d-r fx-fec">
                  <div className="tag downloadData font-ml fx-ccc" onClick={exportToExcel}>
                    <div className="fx-d-r fx-ccc downloadShow" title={t('HISTORY_RECORD.MOBILITY_DOWNLOAD')}>
                      <div className="fx-ccc w-20">
                        <img src={comm_imgs.DB_INFO} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-80 font-ml bold fx-d-c addSwitch_SE inputBox_SE">
                <div className="fx-d-r w-100 fx-ccc SwitchTag">
                  <div
                    className={`fx-ccc outer_active l ${chartSwitch === 0 ? 'dismiss' : ''}`}
                    onClick={() => {
                      setChartSwitch(0);
                    }}>
                    <div className={`fx-ccc l pad-6px  ${chartSwitch === 0 ? 'inner_active' : ''}`} style={{ color: '#fbbc04' }}>
                      {t('HISTORY_RECORD.MOBILITY_SITTING')}
                      {mobilityData.newResult?.sitUpCount[0] ? ` (${mobilityData.newResult?.sitUpCount[0]}${t('HISTORY_RECORD.times')})` : `（0${t('HISTORY_RECORD.times')}）`}
                    </div>
                  </div>
                  <div
                    className={`fx-ccc outer_active in ${chartSwitch === 1 ? '' : 'dismiss'}`}
                    onClick={() => {
                      setChartSwitch(1);
                    }}>
                    <div className={`fx-ccc in ${chartSwitch === 1 ? 'inner_active' : ''}`} style={{ color: '#34a853' }}>
                      {t('HISTORY_RECORD.MOBILITY_LYING')}
                      {mobilityData.newResult?.lieDownCount[0] ? ` (${mobilityData.newResult?.lieDownCount[0]}${t('HISTORY_RECORD.times')})` : `（0${t('HISTORY_RECORD.times')}）`}
                    </div>
                  </div>
                  <div
                    className={`fx-ccc outer_active in add ${chartSwitch === 2 ? '' : 'dismiss'}`}
                    onClick={() => {
                      setChartSwitch(2);
                    }}>
                    <div className={`fx-ccc in ${chartSwitch === 2 ? 'inner_active' : ''}`} style={{ color: '#4285f4' }}>
                      {t('HISTORY_RECORD.MOBILITY_NOT_IN_BED')}
                      {mobilityData.newResult?.leaveBedCount[0] ? ` (${mobilityData.newResult?.leaveBedCount[0]}${t('HISTORY_RECORD.times')})` : `（0${t('HISTORY_RECORD.times')}）`}
                    </div>
                  </div>
                  <div
                    className={`fx-ccc outer_active r add ${chartSwitch === 3 ? '' : 'dismiss'}`}
                    onClick={() => {
                      setChartSwitch(3);
                    }}>
                    <div className={`fx-ccc r ${chartSwitch === 3 ? 'inner_active' : ''}`} style={{ color: '#ffab91' }}>
                      {t('HISTORY_RECORD.MOBILITY_RAW_DATA_ROLL')}
                      {mobilityData.newResult?.turnOverCount[0] ? ` (${mobilityData.newResult?.turnOverCount[0]}${t('HISTORY_RECORD.times')})` : `（0${t('HISTORY_RECORD.times')}）`}
                    </div>
                  </div>
                </div>
              </div>
              <div className="fx-d-r w-100 h-95 border_bgC _SE_top">
                <HisFourIndicatorsChart
                  data={{
                    sitUpCount: mobilityData.newResult.sitUpCount || [0, '0:00:00', []],
                    lieDownCount: mobilityData.newResult.lieDownCount || [0, '0:00:00', []],
                    leaveBedCount: mobilityData.newResult.leaveBedCount || [0, '0:00:00', []],
                    turnOverCount: mobilityData.newResult.turnOverCount || [0, '0:00:00', []],
                  }}
                  chartSwitch={chartSwitch}
                />
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default HisSixIndex;

//判斷資料為空
export function isAllDataEmpty(data: { sitUpCount: any[]; lieDownCount: any[]; leaveBedCount: any[]; turnOverCount: any[] }): boolean {
  if (!data) return true;
  if (Object.keys(data).length === 0) return true;
    const isListEmpty = (list: any[]) => list.length === 3 && list[0] === 0 && list[1] === '0:00:00' && Array.isArray(list[2]) && list[2].length === 0;

  const { sitUpCount, lieDownCount, leaveBedCount, turnOverCount } = data;
  return isListEmpty(sitUpCount) && isListEmpty(lieDownCount) && isListEmpty(leaveBedCount) && isListEmpty(turnOverCount);
}

// 過濾掉所有以'TimesArr'結尾 及residentInfo (用於生成Excel文件)
function filterArrays(data: any) {
  const filteredData: { [key: string]: any } = {};
  for (const key in data) {
    if (!key.endsWith('TimesArr') && !key.endsWith('residentInfo')) {
      filteredData[key] = data[key];
    }
  }
  return filteredData;
}


